// ArgumentLine -- Lane 789. "What this argues" leaves the tab strip.
//
// THE PROBLEM THIS SOLVES, MEASURED (Lane 782). What a deck argues is a
// DECK-level fact. It was living in a SLIDE-level tab strip, third of three
// tabs, in a 336px column -- two clicks away, and structurally the reason the
// tab strip existed at all. Ryan approved the render that pulls it out
// ("the new deck builder thing is much better"), and this is that render, wired.
//
// The line is ALWAYS VISIBLE and reads in four segments -- what we are arguing,
// what has been ruled out, how hard we can push, and what we are offering. Click
// it and it opens FULL-WIDTH.
//
// ⚠️ THE CONTROLS ARE RE-PRESENTED, NEVER REWRITTEN. The expanded body renders
// `NegotiationBriefPanel` VERBATIM -- Lane 766's kill / promote / posture / offer
// controls (cross-tenant isolation mutation-verified) and Lane 773's disclosure +
// Undo are the same component, the same endpoints, the same tests. All this file
// adds is (a) a summary of that same payload, and (b) the width the panel never
// had. Nothing here mutates anything.
//
// ⚠️ TWO KINDS OF RULED-OUT, AND THEY ARE NOT THE SAME KIND OF FACT.
//   * BY YOU  -- a `lever_killed` the buyer stated, quoted in their own words,
//                and RESTORABLE ("Bring this back", inside the panel).
//   * BY THE DATA -- a HARD exclusion the deck's own build gate made
//                (`intake.intent.excluded[].hard`), carried with the gate's
//                verbatim reason. It is NOT restorable and no control is offered,
//                because offering one would imply the buyer can overrule a gate
//                they cannot. Saying "your call" about it would be a lie.
// The render Ryan approved distinguishes them by colour and by tag; so does this.
//
// FLAG-DARK: `fetchBriefAudit` 404s when `companies.negotiation_brief_enabled` is
// FALSE (every tenant today, PROD included) and this renders NOTHING -- the deck
// builder is then byte-identical to a builder that never had an argument line.
import React, { useCallback, useEffect, useState } from 'react';
import { fetchBriefAudit } from '../../api/negotiationBrief';
import NegotiationBriefPanel from './NegotiationBriefPanel';

function Chevron() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.2" aria-hidden="true" focusable="false">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// One truncated sentence for the collapsed line. The full text is always one
// click away in the body, so this never has to be complete -- but it must never
// be MISLEADING, so it ends at a word and marks itself with an ellipsis.
function clip(text, max = 74) {
  const s = String(text || '').trim().replace(/\s+/g, ' ');
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trim()}...`;
}

/**
 * The hard build-gate exclusions, grouped by their verbatim reason.
 *
 * The engine writes ONE gate decision across a pair of slides when the pair
 * travels together (the PPI benchmark and the price-trend chart share a gate,
 * and the reason string says so in its own words). Printing that same paragraph
 * once per slide is what the old panel did; grouping prints it once and names
 * both pages.
 */
export function groupHardExclusions(excluded, nameFor) {
  const groups = [];
  const index = new Map();
  for (const item of excluded || []) {
    if (!item || !item.hard || !item.template_key) continue;
    const reason = String(item.reason || '').trim();
    const key = reason || `__${item.template_key}`;
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ reason, pages: [] });
    }
    groups[index.get(key)].pages.push(
      (nameFor && nameFor(item.template_key)) || item.template_key,
    );
  }
  return groups;
}

/**
 * The collapsed line's four values, derived from the audit payload the panel
 * itself renders. Exported so the derivation is testable without a DOM.
 */
export function summarise(data, hardGroups) {
  const levers = (data && data.levers) || [];
  const promoted = levers.filter((l) => l.status === 'promoted');
  const live = levers.filter((l) => l.status === 'live');
  const killed = levers.filter((l) => l.status === 'killed');
  const postures = (data && data.posture_catalogue) || [];
  const leverage = data && data.leverage;
  const offers = (data && data.offers) || {};
  const committed = offers.committed || [];
  const floated = offers.floated || [];

  let arguing;
  if (promoted.length) arguing = promoted.map((l) => l.label).join(' · ');
  else if (live.length) arguing = live.map((l) => l.label).join(' · ');
  else arguing = 'Nothing is in play';

  const ruledOut = [
    ...killed.map((l) => ({ label: l.label, by: 'you' })),
    ...(hardGroups || []).map((g) => ({ label: g.pages.join(' + '), by: 'data' })),
  ];

  const postureLabel = leverage
    ? ((postures.find((p) => p.posture === leverage.posture) || {}).label
       || leverage.posture)
    : null;

  return {
    arguing,
    arguingCount: promoted.length || live.length,
    ruledOut,
    postureLabel,
    committed: committed.length ? (committed[0].offer_text || committed[0].content) : null,
    floatedCount: floated.length,
  };
}

/**
 * @param {number|null} supplierId    the deck's subject supplier.
 * @param {string|null} supplierName
 * @param {Array}  hardExclusions     `intake.intent.excluded` verbatim -- the
 *   build's own record of what it left out and why. Only `hard: true` entries
 *   are shown: a soft exclusion is a shaping choice, not a ruled-out argument.
 * @param {Function} nameFor          template_key -> the page's display name,
 *   from the registry the assembler already loaded (never a hardcoded map).
 * @param {Function} onChanged        fired after any mutation inside the panel,
 *   so the assembler can mark the preview out of date exactly as before.
 */
function ArgumentLine({
  supplierId, supplierName, hardExclusions = [], nameFor,
  apiBase, token, onChanged,
  popup = false,
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!supplierId) { setData(null); return undefined; }
    fetchBriefAudit(supplierId, { apiBase, token })
      .then((d) => { if (!cancelled) setData(d); })
      // A read failure here must not put an error band above the deck: the panel
      // below reports its own, and this line has nothing to say without data.
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [supplierId, apiBase, token, tick]);

  // The panel is the source of truth and every mutation returns the whole
  // refreshed payload -- but it hands that payload to no one, so this re-reads.
  // One extra GET per edit, and the line can never disagree with the panel.
  const handleChanged = useCallback(() => {
    setTick((n) => n + 1);
    if (onChanged) onChanged();
  }, [onChanged]);

  const hardGroups = groupHardExclusions(hardExclusions, nameFor);

  // The explicit popup mounts the same panel even while its summary read is
  // pending/unavailable; the workspace-only line keeps V1's dark behavior.
  if (popup) return (
    <div data-testid="argument-popup-body">
      {data && Array.isArray(data.levers) ? (
        <p className="dk-argument-lead" data-testid="argument-line-arguing">
          {summarise(data, []).arguing}
        </p>
      ) : <p className="dk-argument-lead">{supplierId
        ? 'The argument summary is not available yet.'
        : 'Choose a supplier in Deck setup to view its argument.'}</p>}
      <NegotiationBriefPanel supplierId={supplierId} supplierName={supplierName}
        apiBase={apiBase} token={token} onChanged={handleChanged} />
    </div>
  );

  // Dark, unavailable, no subject yet, or a payload that is not the audit
  // payload -> render nothing at all. The `levers` check is not paranoia: a line
  // that reads "Nothing is in play" off a shape it did not understand would be
  // stating something about the deck that it has no basis for.
  if (!supplierId || !data || !Array.isArray(data.levers)) return null;

  const s = summarise(data, hardGroups);

  const segments = (
    <>
        <span className="dk-arg-seg">
          <span className="dk-arg-k">Arguing</span>
          <span className="dk-arg-v" data-testid="argument-line-arguing">{s.arguing}</span>
        </span>
        <span className="dk-arg-seg">
          <span className="dk-arg-k">Ruled out</span>
          <span className="dk-arg-v dim" data-testid="argument-line-ruledout">
            {s.ruledOut.length === 0 ? 'Nothing' : s.ruledOut.map((r, i) => (
              <React.Fragment key={`${r.by}-${r.label}`}>
                {i > 0 ? ' · ' : ''}
                {r.label}
                <span className={`dk-tag ${r.by}`}>
                  {r.by === 'you' ? 'your call' : 'data'}
                </span>
              </React.Fragment>
            ))}
          </span>
        </span>
        <span className="dk-arg-seg narrow">
          <span className="dk-arg-k">How hard we can push</span>
          <span className={`dk-arg-v${s.postureLabel ? '' : ' dim'}`}
            data-testid="argument-line-posture">
            {s.postureLabel || 'Not set'}
          </span>
        </span>
        <span className="dk-arg-seg narrow">
          <span className="dk-arg-k">Offering</span>
          <span className={`dk-arg-v${s.committed ? '' : ' dim'}`}
            data-testid="argument-line-offering">
            {s.committed ? clip(s.committed, 48) : 'Nothing approved'}
            {!s.committed && s.floatedCount > 0 ? (
              <span className="dk-tag you">
                {s.floatedCount === 1 ? '1 idea unapproved' : `${s.floatedCount} ideas unapproved`}
              </span>
            ) : null}
          </span>
        </span>
    </>
  );

  return (
    <div className={`dk-arg${open ? ' open' : ''}`}
      data-testid="argument-line">
      <button
        type="button"
        className="dk-arg-bar"
        aria-expanded={open}
        data-testid="argument-line-bar"
        onClick={() => setOpen((v) => !v)}
      >
        {segments}
        <span className="dk-arg-chev"><Chevron /></span>
      </button>

      {open && (
        <div className="dk-arg-body" data-testid="argument-line-body">
          {hardGroups.length > 0 && (
            <div className="dk-arg-grid" style={{ marginBottom: '12px' }}>
              {hardGroups.map((g) => (
                <div className="dk-arg-card" key={g.pages.join('|')}
                  data-testid="argument-line-hard-exclusion">
                  <h4>Ruled out: the data</h4>
                  <div className="dk-arg-lever">{g.pages.join(' + ')}</div>
                  {g.reason ? <div className="dk-quote data">{g.reason}</div> : null}
                  {/* No control. A build gate is not the buyer's to overrule,
                      and a button that could not honour the click would be
                      worse than none. */}
                  <div className="dk-who">
                    Not your call. The deck&rsquo;s own check left
                    {g.pages.length === 1 ? ' this page' : ' these pages'} out.
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Lane 766 + Lane 773, verbatim. */}
          <NegotiationBriefPanel
            supplierId={supplierId}
            supplierName={supplierName}
            apiBase={apiBase}
            token={token}
            onChanged={handleChanged}
          />

          <div className="dk-arg-note">
            Every line here is internal. Two of them (why you ruled an
            argument out, and how hard you can push) would hand
            {supplierName ? ` ${supplierName}` : ' the supplier'} your rebuttal in
            writing. They are in the prep pack you keep. They are not in the deck
            you share.
          </div>
        </div>
      )}
    </div>
  );
}

export default ArgumentLine;
